import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { get, isoDay, addDays } from "@/lib/api";
import { STATUS_HEX } from "@/components/badges";
import { useEventDrawer } from "@/components/EventDrawer";
import { Button } from "@/components/ui/button";

const WD = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const startOfWeek = (d) => addDays(d, -((d.getDay() + 6) % 7));

const range = (mode, ref) => {
  if (mode === "day") return [ref, ref];
  if (mode === "week") { const s = startOfWeek(ref); return [s, addDays(s, 6)]; }
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const s = startOfWeek(first);
  return [s, addDays(s, 41)];
};

const Pill = ({ e, onClick }) => (
  <button data-testid={`cal-event-${e.id}`} onClick={onClick} title={e.title}
    className="w-full text-left text-[10px] leading-tight truncate px-1 py-0.5 rounded-[2px] border-l-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-150"
    style={{ borderLeftColor: e.weak_signal ? "#4f46e5" : STATUS_HEX[e.status] || "#94a3b8" }}>
    <span className="font-mono text-slate-500">{e.start_time || ""}</span> {e.title}
  </button>
);

export default function CalendarView() {
  const [mode, setMode] = useState(() => (window.innerWidth < 640 ? "week" : "month"));
  const [ref, setRef] = useState(new Date());
  const { open } = useEventDrawer();
  const [s, e] = range(mode, ref);
  const { data } = useQuery({
    queryKey: ["events", "cal", isoDay(s), isoDay(e)],
    queryFn: () => get("/events", { date_from: isoDay(s), date_to: isoDay(e), page_size: 2000, with_dates: 1, include_past: 1 }),
  });
  const byDay = {};
  (data?.items || []).forEach((ev) => (ev.occurrence_dates || []).forEach((d) => { (byDay[d] = byDay[d] || []).push(ev); }));
  const step = (n) => setRef(mode === "month" ? new Date(ref.getFullYear(), ref.getMonth() + n, 1) : addDays(ref, n * (mode === "week" ? 7 : 1)));
  const days = [];
  for (let d = s; d <= e; d = addDays(d, 1)) days.push(d);
  const today = isoDay(new Date());
  const title = mode === "month" ? ref.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : mode === "week" ? `Semaine du ${s.toLocaleDateString("fr-FR")}` : ref.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="p-4 lg:p-6 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-display text-xl font-bold capitalize mr-3" data-testid="calendar-title">{title}</h1>
        <Button data-testid="cal-prev" size="sm" variant="outline" className="h-7" onClick={() => step(-1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
        <Button data-testid="cal-today" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRef(new Date())}>Aujourd'hui</Button>
        <Button data-testid="cal-next" size="sm" variant="outline" className="h-7" onClick={() => step(1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
        <div className="ml-auto flex border border-slate-200 dark:border-slate-700 rounded-[3px] overflow-hidden">
          {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"]].map(([k, l]) => (
            <button key={k} data-testid={`cal-mode-${k}`} onClick={() => setMode(k)} className={`px-3 h-7 text-xs transition-colors duration-150 ${mode === k ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      {mode === "day" ? (
        <div className="panel p-3 space-y-1" data-testid="cal-day">
          {(byDay[isoDay(ref)] || []).sort((a, b) => (a.start_time || "99").localeCompare(b.start_time || "99")).map((ev) => <Pill key={ev.id} e={ev} onClick={() => open(ev.id)} />)}
          {!(byDay[isoDay(ref)] || []).length && <div className="text-xs text-slate-400 py-8 text-center">Aucun événement ce jour.</div>}
        </div>
      ) : (
        <div className={`grid ${mode === "week" ? "grid-cols-1 sm:grid-cols-7" : "grid-cols-7"} border-l border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900`} data-testid="cal-grid">
          {WD.map((w) => <div key={w} className={`overline px-2 py-1.5 border-r border-b border-slate-200 dark:border-slate-800 ${mode === "week" ? "hidden sm:block" : ""}`}>{w}</div>)}
          {days.map((d) => {
            const k = isoDay(d);
            const evs = byDay[k] || [];
            const out = mode === "month" && d.getMonth() !== ref.getMonth();
            return (
              <div key={k} className={`border-r border-b border-slate-200 dark:border-slate-800 p-1 ${mode === "week" ? "sm:min-h-[420px]" : "min-h-[64px] sm:min-h-[110px]"} ${out ? "bg-slate-50/60 dark:bg-slate-950/40" : ""}`}>
                <button onClick={() => { setRef(d); setMode("day"); }} className={`font-mono text-[11px] mb-1 px-1 rounded-[2px] ${k === today ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : out ? "text-slate-300" : "text-slate-600"}`}>{mode === "week" && <span className="sm:hidden mr-1 capitalize">{d.toLocaleDateString("fr-FR", { weekday: "long" })}</span>}{d.getDate()}</button>
                <div className="space-y-0.5">
                  {mode === "week" && !evs.length && <div className="sm:hidden text-[10px] text-slate-400 px-1">Aucun événement</div>}
                  {evs.slice(0, mode === "week" ? 30 : 4).map((ev) => <span key={ev.id} className={mode === "month" ? "hidden sm:block" : ""}><Pill e={ev} onClick={() => open(ev.id)} /></span>)}
                  {mode === "month" && evs.length > 0 && <button className="sm:hidden block w-full text-center font-mono text-[10px] rounded-[2px] bg-slate-100 dark:bg-slate-800" onClick={() => { setRef(d); setMode("day"); }}>{evs.length}</button>}
                  {mode === "month" && evs.length > 4 && <button className="hidden sm:block text-[10px] text-slate-500 px-1" onClick={() => { setRef(d); setMode("day"); }}>+{evs.length - 4} autres</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
